import { ApiFilterResponse } from "@interfaces/filter.types";
import { CACHE_REVALIDATION } from "@constants/cacheConfig";
import { FILTERS_URL } from "./endpoints";

const FILTER_DATA_CACHE_TTL_MS = CACHE_REVALIDATION.FILTER_DATA * 1000;

type FilterDataCache = {
  data: ApiFilterResponse | null;
  expiresAt: number;
  promise: Promise<ApiFilterResponse | null> | null;
};

const globalFilterDataCache: FilterDataCache = {
  data: null,
  expiresAt: 0,
  promise: null,
};

interface FetchFiltersOptions {
  throwOnClientError?: boolean;
}

export class FilterServiceHttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "FilterServiceHttpError";
    this.status = status;
  }
}

export const isFilterClientError = (
  error: unknown
): error is FilterServiceHttpError => {
  return (
    error instanceof FilterServiceHttpError &&
    error.status >= 400 &&
    error.status < 500
  );
};

export const fetchFiltersFromAPI = async (
  collectionSlug?: string,
  options: FetchFiltersOptions = {}
): Promise<ApiFilterResponse | null> => {
  try {
    const queryParams = new URLSearchParams();

    if (collectionSlug) {
      const decodedSlug = decodeURIComponent(collectionSlug);
      queryParams.append("slug", decodedSlug);
    }

    const apiUrl = `${FILTERS_URL}?${queryParams.toString()}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new FilterServiceHttpError(
        response.status,
        `API returned status ${response.status}`
      );
    }

    const data = await response.json();

    return data;
  } catch (error: any) {
    if (options.throwOnClientError && isFilterClientError(error)) {
      throw error;
    }

    console.error("[Filter Service] ❌ Error fetching filter options:", error?.message);
    return null;
  }
};

const getGlobalFiltersFromMemoryCache =
  async (): Promise<ApiFilterResponse | null> => {
    const now = Date.now();

    if (globalFilterDataCache.data && globalFilterDataCache.expiresAt > now) {
      return globalFilterDataCache.data;
    }

    if (globalFilterDataCache.promise) {
      return globalFilterDataCache.promise;
    }

    globalFilterDataCache.promise = fetchFiltersFromAPI()
      .then((data) => {
        if (data) {
          globalFilterDataCache.data = data;
          globalFilterDataCache.expiresAt = Date.now() + FILTER_DATA_CACHE_TTL_MS;
        }

        return data;
      })
      .finally(() => {
        globalFilterDataCache.promise = null;
      });

    return globalFilterDataCache.promise;
  };

export const filterService = {
  getFilters: async (
    collectionSlug?: string,
    options: FetchFiltersOptions = {}
  ) => {
    if (!collectionSlug && !options.throwOnClientError) {
      return getGlobalFiltersFromMemoryCache();
    }

    return fetchFiltersFromAPI(collectionSlug, options);
  },
};
